// Agenda/util.js
//
// Formato y conversión de horas del agendamiento. Todo se muestra en hora de Chile,
// sin importar la zona del navegador, y lo que se ingresa ("bloquear de 10:00 a
// 13:00") se interpreta como hora de Chile.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ZONA = 'America/Santiago';

export const DIAS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

const fmt = (opciones) => new Intl.DateTimeFormat('es-CL', { timeZone: ZONA, ...opciones });
const fFecha = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
const fFechaLarga = fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fFechaCorta = fmt({ day: '2-digit', month: '2-digit', year: 'numeric' });
const fHora = fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

export const fecha = (iso) => (iso ? fFecha.format(new Date(iso)) : '—');
export const fechaLarga = (iso) => (iso ? fFechaLarga.format(new Date(iso)) : '—');
export const fechaCorta = (iso) => (iso ? fFechaCorta.format(new Date(iso)) : '—');
export const hora = (iso) => (iso ? fHora.format(new Date(iso)) : '—');
export const fechaHora = (iso) => (iso ? `${fecha(iso)} ${hora(iso)}` : '—');
export const pesos = (n) => `$${Number(n || 0).toLocaleString('es-CL')}`;

/** Un dayjs elegido en un picker, leído como hora de reloj de Chile → ISO real. */
export const comoHoraChile = (d) => dayjs.tz(d.format('YYYY-MM-DD HH:mm'), ZONA).toISOString();

/** Un rango de días elegido en un picker → [00:00 del primero, 00:00 del día siguiente al último), en Chile. */
export const comoDiasChile = ([d, h]) => [
  dayjs.tz(d.format('YYYY-MM-DD'), ZONA).toISOString(),
  dayjs.tz(h.add(1, 'day').format('YYYY-MM-DD'), ZONA).toISOString(),
];

/** "lun 3 nov 09:00 → mié 5 nov 13:00", o "lun 3 nov → dom 16 nov" si son días completos. */
export const textoBloqueo = ({ desde, hasta }) => {
  if (hora(desde) === '00:00' && hora(hasta) === '00:00') {
    const ultimo = new Date(new Date(hasta).getTime() - 12 * 3_600_000); // cae en el último día, con o sin cambio de horario
    return fecha(desde) === fecha(ultimo) ? `${fecha(desde)} (día completo)` : `${fecha(desde)} → ${fecha(ultimo)}`;
  }
  return `${fechaHora(desde)} → ${fechaHora(hasta)}`;
};

/** Horas cada 15 min para los selectores de disponibilidad. */
export const OPCIONES_HORA = Array.from({ length: (23 - 6) * 4 + 1 }, (_, i) => {
  const m = 6 * 60 + i * 15;
  const v = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return { value: v, label: v };
});

// Chips: [tono, texto]. Mismo lenguaje que la ficha de usuario:
// azul = al día · amarillo = en curso/atención · gris = terminado · rojo = falló.
export const ESTADOS = {
  retenida: ['gris', 'Retenida sin pagar'],
  pagando: ['amarillo', 'En pago'],
  confirmada: ['azul', 'Confirmada'],
  pagada_sin_hora: ['amarillo', 'Pagada sin hora'],
  liberada: ['gris', 'Liberada'],
  cancelada: ['rojo', 'Cancelada'],
};

export const BOLETA = {
  no_aplica: ['gris', '—'],
  pendiente: ['amarillo', 'Pendiente'],
  emitiendo: ['amarillo', 'Emitiendo'],
  emitida: ['azul', 'Emitida'],
  error: ['rojo', 'Con error'],
  incierta: ['rojo', 'Verificar en Bsale'],
};

export const RESULTADO_PAGO = {
  aprobado: 'Aprobado',
  rechazado: 'Rechazado',
  anulado: 'Anulado por la persona',
  vencido: 'Venció en Webpay',
  sin_pago: 'Sin pago (según Transbank)',
};

export const MEDIO = { debito: 'Débito', credito: 'Crédito', prepago: 'Prepago' };

/** Tarifa con que se cobró la reserva (cita.tarifa.tipo). */
export const TARIFA = {
  normal: ['gris', 'Precio normal'],
  vecino: ['azul', 'Precio vecino'],
  adulto_mayor: ['amarillo', 'Adulto mayor (+60)'],
};
export const VECINO_PADRON = {
  VIGENTE: 'Tarjeta Vecino vigente',
  NO_VIGENTE: 'Tarjeta Vecino no vigente',
  VECINO_NO_EXISTE: 'Sin Tarjeta Vecino',
  ERROR: 'El padrón no respondió: se cobró sin precio vecino',
};

export const TIPO_TARJETA = {
  VD: 'Débito', VP: 'Prepago', VN: 'Crédito', VC: 'Crédito en cuotas',
  SI: 'Crédito 3 cuotas sin interés', S2: 'Crédito 2 cuotas sin interés', NC: 'Crédito N cuotas sin interés',
};

export const ACCIONES_HISTORIAL = {
  retenida: 'Eligió la hora',
  retencion_liberada: 'Soltó la hora antes de pagar',
  retencion_vencida: 'No pagó a tiempo',
  pagando: 'Pasó a Webpay',
  webpay_no_disponible: 'Webpay no respondió',
  confirmada: 'Pago aprobado, hora confirmada',
  pagada_sin_hora: 'Pago aprobado, pero la hora ya no estaba',
  pago_rechazado: 'Pago rechazado',
  pago_anulado: 'Anuló en Webpay',
  pago_vencido: 'Venció el formulario de Webpay',
  pago_sin_pago: 'Transbank informó que no hubo pago',
  reprogramada: 'Cambio de hora',
  agendada: 'Eligió su hora',
  cancelada: 'Cancelada',
  asistencia: 'Asistencia registrada',
  boleta_emitida: 'Boleta emitida',
  boleta_existe: 'Boleta verificada en Bsale',
  boleta_no_existe: 'Boleta devuelta a la cola',
};
